import { useEffect, useState } from 'react';
import type { PhotoDto } from '@/api/generated';
import { usePhotos, usePhotoThumbnailUrl } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatShortDate, toNumber } from '@/lib/format';
import { PhotoLightbox, PhotoUploadDialog } from './PhotoDialogs';
import styles from './Photos.module.css';

const PHASE_LABEL: Record<string, string> = {
  Voor: 'Voorber.',
  Ontw: 'Ontwerp',
  Verg: 'Vergunningen',
  Aanb: 'Aanbest.',
  Ruw: 'Ruwbouw',
  Wwd: 'W&W-d.',
  Tech: 'Technieken',
  Afw: 'Afwerking',
  Opl: 'Oplevering',
  Naz: 'Nazorg',
};

export function Photos() {
  const photos = usePhotos({ pageSize: 100 });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [active, setActive] = useState<PhotoDto | null>(null);

  // Keep the lightbox's photo prop in sync with the freshest list data:
  // after an inline edit the photos query invalidates and refetches, but
  // `active` would otherwise still hold the pre-edit snapshot.
  useEffect(() => {
    if (!active) return;
    const fresh = photos.data?.items.find((p) => p.id === active.id);
    if (!fresh) {
      setActive(null);
      return;
    }
    if (
      fresh.caption !== active.caption ||
      fresh.room !== active.room ||
      fresh.phaseId !== active.phaseId
    ) {
      setActive(fresh);
    }
  }, [photos.data, active]);

  return (
    <AppFrame>
      <QueryBoundary query={photos} loading="Foto's laden…">
        {(page) => {
          const total = toNumber(page.total);
          const grouped = groupByDate(page.items);
          const groups = Array.from(grouped.entries()).sort((a, b) => b[0].localeCompare(a[0]));
          const stats = computeStats(page.items);

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">
                    Foto-dagboek · {total} foto's · {groups.length} werfbezoeken
                  </div>
                  <h1 className={styles.title}>Foto-dagboek</h1>
                  <p className={styles.subtitle}>
                    Bewijsmateriaal voor latere discussies — verborgen gebreken, oplevering,
                    garantieclaim.
                  </p>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <div className={`row gap-4 ${styles.segment}`}>
                    <Btn variant="ghost" className={`${styles.segmentBtn} ${styles.segmentActive}`}>
                      Tijdlijn
                    </Btn>
                    <Btn variant="ghost" className={styles.segmentBtn}>
                      Per fase
                    </Btn>
                    <Btn variant="ghost" className={styles.segmentBtn}>
                      Op plan
                    </Btn>
                  </div>
                  <Btn variant="primary" onClick={() => setUploadOpen(true)}>
                    <Icon name="camera" size={14} /> Foto toevoegen
                  </Btn>
                </div>
              </div>
              <PhotoUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
              {active && (
                <PhotoLightbox
                  open={active !== null}
                  onOpenChange={(o) => {
                    if (!o) setActive(null);
                  }}
                  photo={active}
                />
              )}

              <div className={`row gap-16 ${styles.stats}`}>
                {stats.map((s) => (
                  <div key={s.k} className={`row gap-8 ${styles.stat}`}>
                    <span className={styles.statDot} style={{ background: `var(--${s.kind})` }} />
                    <span className={`mono ${styles.statLabel}`}>{s.k}</span>
                    <span className={styles.statValue}>{s.v}</span>
                  </div>
                ))}
              </div>

              <div className="col gap-24">
                {groups.length === 0 ? (
                  <div className="mono c-ink-3" style={{ padding: '24px' }}>
                    Nog geen foto's geüpload.
                  </div>
                ) : (
                  groups.map(([dateKey, rows]) => (
                    <div key={dateKey}>
                      <div className={`row gap-12 ${styles.groupHead}`}>
                        <div className={styles.groupDate}>{formatShortDate(rows[0].takenAt)}</div>
                        <Pill kind="warn" dot={false}>
                          {PHASE_LABEL[rows[0].phaseId] ?? rows[0].phaseId}
                        </Pill>
                        {rows[0].room && <span className={styles.groupSub}>· {rows[0].room}</span>}
                        <span className={`mono ml-auto ${styles.groupCount}`}>
                          {rows.length} foto's
                        </span>
                      </div>
                      <div className={styles.grid}>
                        {rows.map((p, j) => (
                          <PhotoTile key={p.id} photo={p} index={j} onOpen={() => setActive(p)} />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function PhotoTile({
  photo,
  index,
  onOpen,
}: {
  photo: PhotoDto;
  index: number;
  onOpen: () => void;
}) {
  const thumb = usePhotoThumbnailUrl(photo.id, 320);
  const label = photo.caption ?? `foto ${index + 1}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`ph ${styles.tile} ${styles.tileButton}`}
      aria-label={label}
    >
      {thumb.data ? (
        <img src={thumb.data} alt={label} className={styles.tileImage} loading="lazy" />
      ) : (
        <span className={styles.tilePlaceholder}>{label}</span>
      )}
      {index === 0 && photo.room && <div className={styles.planTag}>📍 plan</div>}
    </button>
  );
}

function groupByDate(photos: PhotoDto[]): Map<string, PhotoDto[]> {
  const out = new Map<string, PhotoDto[]>();
  for (const p of photos) {
    const key = p.takenAt.slice(0, 10);
    const arr = out.get(key) ?? [];
    arr.push(p);
    out.set(key, arr);
  }
  return out;
}

function computeStats(
  photos: PhotoDto[],
): Array<{ k: string; v: string; kind: 'warn' | 'idle' | 'info' | 'ok' }> {
  const weekAgo = Date.now() - 7 * 86_400_000;
  const thisWeek = photos.filter((p) => new Date(p.takenAt).getTime() > weekAgo).length;
  const ruw = photos.filter((p) => p.phaseId === 'Ruw').length;
  return [
    { k: 'Deze week', v: `${thisWeek} foto's`, kind: 'warn' },
    { k: 'Ruwbouw totaal', v: `${ruw} foto's`, kind: 'idle' },
    { k: 'Vóór-na vergelijkingen', v: '6 sets', kind: 'info' },
    {
      k: 'Met locatie op plan',
      v: String(photos.filter((p) => p.room !== null).length),
      kind: 'ok',
    },
  ];
}
